import React, { memo, useEffect, useRef, useState } from 'react';

import { API } from 'constants';
import client from '../../../../services/cycle-api';
import organizationClient from '../../../../services/organization-api';

import useUser from '../../../../hooks/useUser';
import { useFormState } from '../../../../hooks/useFormState';
import { useSwrRequestKeysToMutateFromContext } from '../../../../hooks/useSwrRequestKeysToMutateFromContext';
import { useFetchOptionsForPaginatedSelectWithSWR } from '../../../../hooks/useFetchOptionsForPaginatedSelectWithSWR';

import { checkIfFormDataChanged } from '../../../../helpers/checkIfFormDataChanged';
import { mutateListDataAfterUpdatingItem } from '../../../../helpers/swrMutateUtils';

import { defaultErrorMessage, emptyFieldErrorMessage } from '../../../../constants/errorMessages';

import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import PaginatedSelect from '../../../../design-system/PaginatedSelect/PaginatedSelect';
import SectionContainer from '../../PlaybookDetailPage/SectionContainer/SectionContainer';
import { Button, Input, TextArea } from '../../../../design-system';

const OverviewTabSection = ({ cycleDetail, setCycleDetail }) => {
    const { id, name, description, owned_by } = cycleDetail;

    const { formData, handleInputChange, fieldErrorMessages, setFieldErrorMessages } = useFormState(
        () => ({ name, description, owned_by })
    );
    const originalFormData = useRef(formData);

    const [areChanges, setAreChanges] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    const { user } = useUser();

    const {
        requestKeysToMutate: { cycles: requestKeysToMutate },
    } = useSwrRequestKeysToMutateFromContext();

    const {
        options: usersOptions,
        optionsLoading,
        totalOptions,
        setPage,
        canLoadMoreOptions,
    } = useFetchOptionsForPaginatedSelectWithSWR({
        client: organizationClient,
        route: API.ROUTES.organization.organizationMembership,
        searchParams: { organization: user?.organization?.id },
        formatResponseToOptions,
        initialOptions: [{ id: owned_by?.id, name: owned_by?.email, fullName: owned_by?.name }],
    });

    function formatResponseToOptions(res) {
        return res?.map(({ user: { pk, email, first_name, last_name } }) => {
            const fullName = `${first_name}${last_name ? ' ' : ''}${last_name}`.trim() || null;
            return { id: pk, name: email, fullName };
        });
    }

    useEffect(() => {
        const isFormDataChanged = checkIfFormDataChanged(originalFormData.current, formData);

        if (isFormDataChanged !== areChanges) {
            setAreChanges(isFormDataChanged);
        }
    }, [formData]);

    const handleSaveChanges = async () => {
        try {
            const areEmptyFields = Object.values(formData).some((value) => !value);

            const { name, description, owned_by } = formData;
            if (areEmptyFields) {
                setFieldErrorMessages({
                    name: !name ? emptyFieldErrorMessage : null,
                    description: !description ? emptyFieldErrorMessage : null,
                    owned_by: !owned_by?.id ? 'Please select a Cycle Owner' : null,
                });
                return;
            }

            setIsLoading(true);

            const payload = { name, description, owned_by: owned_by?.id };

            const { data } = await client.patch(`${API.ROUTES.cycle.cycle}${id}/`, payload);

            setCycleDetail((prevData) => ({
                ...prevData,
                name: data.name,
                description: data.description,
                owned_by: formData.owned_by, // use owned_by from form data, because response includes fust id
            }));
            originalFormData.current = formData;
            setAreChanges(false);

            mutateListDataAfterUpdatingItem({
                requestKeysToMutate,
                id,
                updatedPart: {
                    name: data.name,
                    description: data.description,
                },
            });
        } catch (e) {
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOwnerChange = (_, userData) => {
        const formattedData = { id: userData.id, name: userData.fullName, email: userData.name };
        handleInputChange('owned_by', formattedData);
    };

    const submitButtonState = isLoading ? 'loading' : areChanges ? 'default' : 'disabled';

    return (
        <SectionContainer
            title="About Cycle"
            variant="secondary"
            marginClassName="mb-2"
            paddingClassName="p-4 sm:p-5 md:p-6"
        >
            <div className="flex flex-col gap-4 w-full max-w-[622px]">
                <Input
                    size="md"
                    name="cycle_name"
                    value={formData.name}
                    label="Cycle Name"
                    isRequired
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter Cycle name"
                    state={fieldErrorMessages.name ? 'error' : 'default'}
                    errorMessage={fieldErrorMessages.name}
                />

                <TextArea
                    name="cycle_description"
                    value={formData.description}
                    label="Cycle Description"
                    isRequired
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter Cycle description"
                    state={fieldErrorMessages.description ? 'error' : 'default'}
                    errorMessage={fieldErrorMessages.description}
                    height="120px"
                />

                <PaginatedSelect
                    size="md"
                    name="owned_by"
                    value={formData.owned_by?.id}
                    options={usersOptions}
                    onChange={handleOwnerChange}
                    state={fieldErrorMessages.owned_by ? 'error' : 'default'}
                    errorMessage={fieldErrorMessages.owned_by}
                    label="Cycle Owner"
                    isRequired
                    placeholder="Select a Cycle Owner"
                    fetchOptions={() => setPage((page) => page + 1)}
                    canLoadMore={canLoadMoreOptions}
                    optionsLoading={optionsLoading}
                    includeClientSideFiltering
                    totalOptionsCount={totalOptions}
                    dropdownHeight={180}
                    isAdditionalLoaderDisplayed={optionsLoading}
                />

                <div>
                    <Button
                        type="primary"
                        size="xs"
                        text="Save Changes"
                        state={submitButtonState}
                        onClick={handleSaveChanges}
                    />
                </div>
            </div>

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </SectionContainer>
    );
};

export default memo(OverviewTabSection);
